import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { FaPlus, FaMinus, FaExpandArrowsAlt, FaRegCompass, FaRegEye } from 'react-icons/fa';
import './SimplePanoramaWithZoom.css';

const SimplePanoramaWithZoom = ({ imageUrl, minZoom = 60, maxZoom = 120,
    zoomSensitivity = 0.05, dragSensitivity = 0.05,
    initialLongitude = 0,
    initialLatitude = 0,
    sphereWidthSegments = 120,
    sphereHeightSegments = 80,
    usePixelRatio = false,
}) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const cameraRef = useRef(new THREE.PerspectiveCamera(80, 0, 0.1, 500));
    const sceneRef = useRef(new THREE.Scene());
    const rendererRef = useRef(null);
    const isUserInteracting = useRef(false);
    const onMouseDownMouseX = useRef(0);
    const onMouseDownMouseY = useRef(0);
    const targetLon = useRef(initialLongitude);
    const targetLat = useRef(initialLatitude);
    const currentLon = useRef(initialLongitude);
    const currentLat = useRef(initialLatitude);
    const animationFrameId = useRef(null);
    const isTextureLoaded = useRef(false);
    const ongoingTouches = useRef([]);
    const touchZoomSensitivity = 0.01;
    const sphereRef = useRef(null);
    const raycaster = useRef(new THREE.Raycaster());
    const mouse = useRef(new THREE.Vector2());
    const rotationSmoothness = 0.10;
    const textureRef = useRef(null);

    const [currentZoom, setCurrentZoom] = useState(75);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [show360Icon, setShow360Icon] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const copyTouch = useCallback(({ identifier, clientX, clientY }) => ({ identifier, clientX, clientY }), []);
    const getDistance = useCallback((touch1, touch2) => Math.sqrt(Math.pow(touch1.clientX - touch2.clientX, 2) + Math.pow(touch1.clientY - touch2.clientY, 2)), []);
    const findTouchIndex = useCallback((touchList, identifier) => touchList.findIndex(t => t.identifier === identifier), []);
    const findTouch = useCallback((touchList, identifier) => touchList.find(t => t.identifier === identifier), []);

    const lastMouseX = useRef(0);
    const lastMouseY = useRef(0);
    const velocityLon = useRef(0);
    const velocityLat = useRef(0);
    const decelerationFactor = useRef(0.95);


    const handleTouchStart = useCallback((event) => {
        const touches = event.changedTouches;
        for (let i = 0; i < touches.length; i++) {
            ongoingTouches.current.push(copyTouch(touches[i]));
        }
    }, [copyTouch, ongoingTouches]);

    const handleTouchMove = useCallback((event) => {
        const touches = event.changedTouches;
        let pinchDistance = null;
        let prevPinchDistance = null;

        if (ongoingTouches.current.length >= 2 && touches.length >= 2) {
            const touch1Index = findTouchIndex(ongoingTouches.current, touches[0].identifier);
            const touch2Index = findTouchIndex(ongoingTouches.current, touches[1].identifier);

            if (touch1Index !== -1 && touch2Index !== -1) {
                const touch1 = ongoingTouches.current[touch1Index];
                const touch2 = ongoingTouches.current[touch2Index];
                const currentTouch1 = findTouch(touches, touch1.identifier);
                const currentTouch2 = findTouch(touches, touch2.identifier);

                if (currentTouch1 && currentTouch2) {
                    pinchDistance = getDistance(currentTouch1, currentTouch2);
                    prevPinchDistance = getDistance(touch1, touch2);
                }
            }
        }

        if (pinchDistance !== null && prevPinchDistance !== null) {
            const deltaDistance = pinchDistance - prevPinchDistance;
            setCurrentZoom((prevZoom) =>
                Math.max(minZoom, Math.min(maxZoom, prevZoom - deltaDistance * touchZoomSensitivity * 100))
            );
        }

        for (let i = 0; i < touches.length; i++) {
            const idx = findTouchIndex(ongoingTouches.current, touches[i].identifier);
            if (idx !== -1) {
                ongoingTouches.current.splice(idx, 1, copyTouch(touches[i]));
            }
        }
    }, [copyTouch, findTouch, findTouchIndex, getDistance, maxZoom, minZoom, ongoingTouches, setCurrentZoom, touchZoomSensitivity]);

    const handleTouchEnd = useCallback((event) => {
        const touches = event.changedTouches;
        for (let i = 0; i < touches.length; i++) {
            const idx = findTouchIndex(ongoingTouches.current, touches[i].identifier);
            if (idx !== -1) {
                ongoingTouches.current.splice(idx, 1);
            }
        }
    }, [findTouchIndex, ongoingTouches]);

    const handleTouchCancel = useCallback((event) => {
        const touches = event.changedTouches;
        for (let i = 0; i < touches.length; i++) {
            const idx = findTouchIndex(ongoingTouches.current, touches[i].identifier);
            if (idx !== -1) {
                ongoingTouches.current.splice(idx, 1);
            }
        }
    }, [findTouchIndex, ongoingTouches]);

    const animate = useCallback(() => {
        if (isUserInteracting.current) {
            currentLon.current += (targetLon.current - currentLon.current) * rotationSmoothness;
            currentLat.current += (targetLat.current - currentLat.current) * rotationSmoothness;
        } else {
            currentLon.current += velocityLon.current;
            currentLat.current += velocityLat.current;
            velocityLon.current *= decelerationFactor.current;
            velocityLat.current *= decelerationFactor.current;

            if (Math.abs(velocityLon.current) < 0.001 && Math.abs(velocityLat.current) < 0.001) {
                velocityLon.current = 0;
                velocityLat.current = 0;
            }
        }

        let lat = Math.max(-85, Math.min(85, currentLat.current));
        const phi = THREE.MathUtils.degToRad(90 - lat);
        const theta = THREE.MathUtils.degToRad(currentLon.current);

        cameraRef.current.position.x = Math.sin(phi) * Math.cos(theta) * 0.1;
        cameraRef.current.position.y = Math.cos(phi) * 0.1;
        cameraRef.current.position.z = Math.sin(phi) * Math.sin(theta) * 0.1;

        cameraRef.current.fov = currentZoom;
        cameraRef.current.updateProjectionMatrix();
        cameraRef.current.lookAt(sceneRef.current.position);
        rendererRef.current?.render(sceneRef.current, cameraRef.current);
        animationFrameId.current = requestAnimationFrame(animate);
    }, [currentZoom, rotationSmoothness]); // Removed dragSensitivity

    const setupRenderer = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const width = container.clientWidth;
        const height = container.clientHeight;
        const pixelRatio = usePixelRatio ? window.devicePixelRatio : 1;

        cameraRef.current.aspect = width / height;
        cameraRef.current.updateProjectionMatrix();

        if (!rendererRef.current) {
            rendererRef.current = new THREE.WebGLRenderer({ canvas, antialias: true });
        }
        rendererRef.current.setSize(width * pixelRatio, height * pixelRatio, false);
        rendererRef.current.setPixelRatio(pixelRatio);
    }, [usePixelRatio]);


    useEffect(() => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        if (!container || !canvas) return;

        const scene = sceneRef.current;
        const camera = cameraRef.current;
        camera.position.set(0, 0, 0.1);

        const textureLoader = new THREE.TextureLoader();
        textureLoader.load(
            imageUrl,
            (texture) => {
                texture.mapping = THREE.EquirectangularReflectionMapping;
                texture.generateMipmaps = true;
                texture.minFilter = THREE.LinearMipmapLinearFilter;
                texture.magFilter = THREE.LinearFilter;
                texture.wrapS = THREE.ClampToEdgeWrapping;
                texture.wrapT = THREE.ClampToEdgeWrapping;

                if (rendererRef.current) {
                    const maxAnisotropy = rendererRef.current.capabilities.getMaxAnisotropy();
                    texture.anisotropy = maxAnisotropy;
                }

                const geometry = new THREE.SphereGeometry(500, sphereWidthSegments, sphereHeightSegments);
                const material = new THREE.MeshStandardMaterial({
                    map: texture,
                    side: THREE.BackSide,
                    roughness: .04,
                    metalness: 0.0,
                });
                const sphere = new THREE.Mesh(geometry, material);
                sphereRef.current = sphere;
                scene.add(sphere);

                const ambientLight = new THREE.AmbientLight(0xffffff, 0.1);
                scene.add(ambientLight);

                isTextureLoaded.current = true;
                textureRef.current = texture;
                setupRenderer();
                setIsLoading(false);
            },
            undefined,
            (error) => {
                console.error('An error happened while loading the texture:', error);
                setIsLoading(false);
            }
        );

        const onPointerDown = (event) => {
            isUserInteracting.current = true;
            onMouseDownMouseX.current = event.clientX;
            onMouseDownMouseY.current = event.clientY;
            targetLon.current = currentLon.current;
            targetLat.current = currentLat.current;
            setShow360Icon(false);
            container.style.cursor = 'grabbing';
            lastMouseX.current = event.clientX;
            lastMouseY.current = event.clientY;
            velocityLon.current = 0;
            velocityLat.current = 0;
        };

        const onPointerMove = (event) => {
            if (isUserInteracting.current) {
                const deltaLon = (onMouseDownMouseX.current - event.clientX) * dragSensitivity;
                const deltaLat = (event.clientY - onMouseDownMouseY.current) * dragSensitivity;

                targetLon.current += deltaLon;
                targetLat.current += deltaLat;

                velocityLon.current = (event.clientX - lastMouseX.current) * dragSensitivity;
                velocityLat.current = (event.clientY - lastMouseY.current) * dragSensitivity;

                lastMouseX.current = event.clientX;
                lastMouseY.current = event.clientY;
            }
        };

        const onPointerUp = () => {
            isUserInteracting.current = false;
            container.style.cursor = 'grab';
        };

        const handleResize = () => {
            setupRenderer();
        };

        let resizeTimeoutId;
        const debouncedResize = () => {
            clearTimeout(resizeTimeoutId);
            resizeTimeoutId = setTimeout(handleResize, 100);
        };

        const zoomStep = 5;
        const handleWheel = (event) => {
            event.preventDefault();
            const deltaY = event.deltaY;
            setCurrentZoom((prevZoom) => {
                const newZoom = deltaY > 0 ? Math.min(maxZoom, prevZoom + zoomStep) : Math.max(minZoom, prevZoom - zoomStep);
                return newZoom;
            });
        };

        const handleCanvasClick = (event) => {
            if (!sphereRef.current || !rendererRef.current || !cameraRef.current) return;

            mouse.current.x = (event.clientX / canvas.clientWidth) * 2 - 1;
            mouse.current.y = -(event.clientY / canvas.clientHeight) * 2 + 1;

            raycaster.current.setFromCamera(mouse.current, cameraRef.current);

            const intersects = raycaster.current.intersectObject(sphereRef.current);

            if (intersects.length > 0) {
                const intersectionPoint = intersects[0].point;

                const phi = Math.acos(intersectionPoint.y / 500);
                const theta = Math.atan2(intersectionPoint.z, intersectionPoint.x);

                let newLat = THREE.MathUtils.radToDeg((Math.PI / 2) - phi);
                let newLon = THREE.MathUtils.radToDeg(theta);
                newLon = (newLon + 180) % 360 - 180;

                targetLon.current = newLon;
                targetLat.current = newLat;
            }
        };

        currentLon.current = initialLongitude;
        currentLat.current = initialLatitude;
        targetLon.current = initialLongitude;
        targetLat.current = initialLatitude;

        container.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
        window.addEventListener('resize', handleResize);
        container.addEventListener('wheel', handleWheel, { passive: false });
        canvas.addEventListener('click', handleCanvasClick);
        container.addEventListener('touchstart', handleTouchStart, { passive: true });
        container.addEventListener('touchmove', handleTouchMove, { passive: true });
        container.addEventListener('touchend', handleTouchEnd, { passive: true });
        container.addEventListener('touchcancel', handleTouchCancel, { passive: true });
        window.addEventListener('resize', debouncedResize);

        setupRenderer();
        animate();

        return () => {
            container.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('resize', handleResize);
            container.removeEventListener('wheel', handleWheel);
            canvas.removeEventListener('click', handleCanvasClick);
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
            container.removeEventListener('touchcancel', handleTouchCancel);
            window.removeEventListener('resize', debouncedResize);
            clearTimeout(resizeTimeoutId);

            cancelAnimationFrame(animationFrameId.current);
            if (rendererRef.current) {
                rendererRef.current.dispose();
                rendererRef.current = null;
            }
            if (sphereRef.current) {
                sphereRef.current.geometry.dispose();
                sphereRef.current.material.dispose();
                sphereRef.current = null;
            }
            if (textureRef.current) {
                textureRef.current.dispose();
                textureRef.current = null;
            }
        };
    }, [imageUrl, animate, setupRenderer, minZoom, maxZoom, zoomSensitivity, dragSensitivity,
        sphereWidthSegments, sphereHeightSegments, usePixelRatio, initialLongitude, initialLatitude,
        handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel,
    ]);

    const handleZoomIn = () => {
        setCurrentZoom((prevZoom) => Math.max(minZoom, prevZoom - 5));
    };

    const handleZoomOut = () => {
        setCurrentZoom((prevZoom) => Math.min(maxZoom, prevZoom + 5));
    };

    const handleFullscreen = () => {
        const container = containerRef.current;
        if (!container) return;

        if (!isFullscreen) {
            container.requestFullscreen?.();
            container.mozRequestFullScreen?.();
            container.webkitRequestFullscreen?.();
            container.msRequestFullscreen?.();
        } else {
            document.exitFullscreen?.();
            document.mozCancelFullScreen?.();
            document.webkitExitFullscreen?.();
            document.msExitFullscreen?.();
        }
        setIsFullscreen((prev) => !prev);
        setTimeout(setupRenderer, 100);
    };

    const handleResetView = () => {
        targetLon.current = initialLongitude;
        targetLat.current = initialLatitude;
        setCurrentZoom(75);
    };

    return (
        <div className="panorama-container" ref={containerRef} style={{ position: 'relative', width: '100%', height: '500px', overflow: 'hidden', cursor: 'grab' }}>
            <canvas className="panorama-canvas" ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%', opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s ease-in-out' }} />
            {isLoading && (
                <div className="loading-spinner">
                    Loading...
                </div>
            )}
            {show360Icon && !isLoading && (
                <div className="center-icon">
                    <FaRegEye size={50} color="rgba(255, 255, 255, 0.8)" />
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginTop: '5px', fontSize: '14px' }}>Drag to Explore</p>
                </div>
            )}
            <div className="controls">
                <button onClick={handleZoomOut} className="control-button">
                    <FaMinus />
                </button>
                <button onClick={handleZoomIn} className="control-button">
                    <FaPlus />
                </button>
                <button onClick={handleResetView} className="control-button">
                    <FaRegCompass />
                </button>
                <button onClick={handleFullscreen} className="control-button">
                    <FaExpandArrowsAlt />
                </button>
            </div>
        </div>
    );
};

export default SimplePanoramaWithZoom;